import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
  computed,
  inject,
  signal
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';

import { AppHeader } from '../../components/header/header';
import { PROJECTS } from '../../data/projects.data';

@Component({
  selector: 'app-project-page',
  standalone: true,
  imports: [
    CommonModule,
    AppHeader
  ],
  templateUrl: './project-page.html',
  styleUrl: './project-page.scss'
})
export class ProjectPage implements OnInit, AfterViewInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);

  scrollY = signal(0);
  viewportHeight = signal(window.innerHeight);

  private rafId: number | null = null;
  private scrollRafId: number | null = null;

  private readonly mobileBreakpoint = 768;

  private get isMobile(): boolean {
    return window.innerWidth <= this.mobileBreakpoint;
  }

  @ViewChild('stage', { static: true })
  private stageRef!: ElementRef<HTMLElement>;

  private readonly onPageShowHandler = (): void => {
    this.syncViewportAndScroll();
  };

  private readonly onVisibilityChangeHandler = (): void => {
    if (!document.hidden) {
      this.syncViewportAndScroll();
    }
  };

  project = computed(() => {
    const slug = this.route.snapshot.paramMap.get('slug');

    return PROJECTS.find(project => project.slug === slug) ?? PROJECTS[0];
  });

  sections = computed(() => this.project().sections);

  pageHeight = computed(() => {

    return (this.sections().length + 1) * this.viewportHeight();
  });

  projectInfo = computed(() => {
    const overview = this.project().overview;

    const getValue = (label: string): string => {
      return overview.items
        .find(item => item.label === label)
        ?.values
        .join(', ') ?? '';
    };

    return {
      description: overview.description.join(' '),
      client: getValue('Client'),
      service:
        getValue('Service') ||
        getValue('Scope') ||
        getValue('Stack'),
      year: getValue('Year'),
      site: getValue('Site')
    };
  });

  siteHref = computed(() => {
    const site = this.projectInfo().site;

    if (!site) {
      return '#';
    }

    return site.startsWith('http')
      ? site
      : `https://${site}`;
  });

  ngOnInit(): void {
    this.resetScrollPosition();

    window.addEventListener('pageshow', this.onPageShowHandler);
    document.addEventListener('visibilitychange', this.onVisibilityChangeHandler);
  }

  ngAfterViewInit(): void {
    this.rafId = window.requestAnimationFrame(() => {
      this.resetScrollPosition();
    });
  }

  ngOnDestroy(): void {
    if (this.rafId !== null) {
      window.cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    if (this.scrollRafId !== null) {
      window.cancelAnimationFrame(this.scrollRafId);
      this.scrollRafId = null;
    }

    window.removeEventListener('pageshow', this.onPageShowHandler);
    document.removeEventListener('visibilitychange', this.onVisibilityChangeHandler);
  }

  @HostListener('window:scroll')
  onScroll(): void {
    if (this.scrollRafId !== null) {
      return;
    }

    this.scrollRafId = window.requestAnimationFrame(() => {
      this.scrollY.set(window.scrollY);
      this.scrollRafId = null;
    });
  }

  @HostListener('window:resize')
  onResize(): void {
    this.syncViewportAndScroll();
  }

  getSectionProgress(index: number): number {
    if (index === 0) {
      return 1;
    }

    const start = (index - 1) * this.viewportHeight();

    const progress =
      (this.scrollY() - start) / this.viewportHeight();

    return Math.max(0, Math.min(1, progress));
  }

  getSectionClipPath(index: number): string {
    if (this.isMobile) {
      return 'none';
    }

    if (index === 0) {
      return 'inset(0 0 0 0)';
    }

    const progress = this.getSectionProgress(index);
    const topInset = (1 - progress) * 100;

    return `inset(${topInset}% 0 0 0)`;
  }

  getSectionZIndex(index: number): number {
    return 100 + index;
  }

  getInfoClipPath(): string {
    return this.getSectionClipPath(this.sections().length);
  }

  getInfoZIndex(): number {
    return this.getSectionZIndex(this.sections().length);
  }

  getSectionOffset(index: number): number {
    if (!this.isMobile || index === 0) {
      return 0;
    }

    const progress = this.getSectionProgress(index);
    const offset = (1 - progress) * this.viewportHeight();
    const dpr = window.devicePixelRatio || 1;

    return Math.round(offset * dpr) / dpr;
  }

  getSectionTransform(index: number): string {
    if (!this.isMobile) {
      return 'none';
    }

    return `translate3d(0, ${this.getSectionOffset(index)}px, 0)`;
  }

  getSectionContentTransform(index: number): string {
    if (!this.isMobile) {
      return 'none';
    }

    return `translate3d(0, ${-this.getSectionOffset(index)}px, 0)`;
  }

  private resetScrollPosition(): void {
    window.scrollTo(0, 0);

    this.viewportHeight.set(this.getViewportHeight());
    this.scrollY.set(0);
  }

  private syncViewportAndScroll(): void {
    this.viewportHeight.set(this.getViewportHeight());
    this.scrollY.set(window.scrollY);
  }

  private getViewportHeight(): number {
    return Math.round(
      this.stageRef?.nativeElement.getBoundingClientRect().height ||
      window.innerHeight
    );
  }
}
